import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ImagePreviewModalProps {
  src: string
  alt?: string
  isOpen: boolean
  onClose: () => void
}

export function ImagePreviewModal({
  src,
  alt = 'Preview',
  isOpen,
  onClose,
}: ImagePreviewModalProps) {
  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleKeyDown = (e: globalThis.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const modalContent = (
    <div
      className="image-preview-modal flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      <div className="relative max-w-[90vw] max-h-[90vh] p-4">
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 p-2 bg-white/10 hover:bg-white/20 rounded-full backdrop-blur-sm transition-colors"
          style={{ zIndex: 2147483647 }}
          aria-label="Close preview"
        >
          <X className="w-5 h-5 text-white" />
        </button>
        <img
          src={src}
          alt={alt}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
          onError={(e) => {
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
          }}
        />
      </div>
    </div>
  )

  // Use portal to render at document body level
  return typeof window !== 'undefined'
    ? createPortal(modalContent, document.body)
    : null
}

interface ImageThumbnailProps {
  src: string
  alt?: string
  className?: string
}

export function ImageThumbnail({
  src,
  alt = 'Thumbnail',
  className,
}: ImageThumbnailProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [imageError, setImageError] = useState(false)

  // Reset states when src changes
  useEffect(() => {
    setImageError(false)
    setIsPreviewOpen(false)
  }, [src])

  const handleImageError = () => {
    setImageError(true)
  }

  const handleThumbnailClick = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent row click
    setIsPreviewOpen(true)
  }

  // Don't render anything if image failed to load
  if (imageError) {
    return <span className="text-xs text-gray-500 italic">Invalid image</span>
  }

  return (
    <>
      <div
        onClick={handleThumbnailClick}
        className={cn(
          'cursor-pointer hover:opacity-80 transition-opacity',
          className,
        )}
      >
        <img
          src={src}
          alt={alt}
          className="w-12 h-12 object-cover rounded border border-gray-300 shadow-sm"
          onError={handleImageError}
        />
      </div>

      <ImagePreviewModal
        src={src}
        alt={alt}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
    </>
  )
}

// Helper function to detect if a string is likely an image URL
export function isImageUrl(url: string): boolean {
  if (typeof url !== 'string' || !url.trim()) return false

  // Check if it's a valid URL
  try {
    new URL(url)
  } catch {
    return false
  }

  // Check for common image extensions
  const imageExtensions = /\.(jpg|jpeg|png|gif|webp|bmp|svg|ico)(\?.*)?$/i
  if (imageExtensions.test(url)) return true

  // Check for common image hosting domains
  const imageHosts = [
    'images.unsplash.com',
    'cdn.pixabay.com',
    'images.pexels.com',
    'i.imgur.com',
    'cdn.jsdelivr.net',
    'raw.githubusercontent.com',
    'avatars.githubusercontent.com',
    'lh3.googleusercontent.com',
    'drive.google.com',
    'dropbox.com',
    'amazonaws.com',
    'cloudfront.net',
    'googleapis.com',
  ]

  return imageHosts.some((host) => url.includes(host))
}
